import { createFileRoute, useCanGoBack, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Languages, MessageCircle, RotateCcw, Send } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@nokowazzi/api-client';
import { formatMoney } from '@nokowazzi/types';
import type { LocaleCode, SendMessageInput } from '@nokowazzi/types';
import {
  ActionSheet,
  Button,
  ChatBubble,
  EmptyState,
  IconButton,
  Input,
  Skeleton,
  Spinner,
  toast,
  TopAppBar,
  TrustBadge,
} from '@nokowazzi/ui';
import type { ChatBubbleMessage } from '@nokowazzi/ui';
import { requireAuthBeforeLoad } from '../features/auth/guard';
import { useProfile } from '../features/auth/useProfile';
import {
  useMarkRead,
  useMessagesInfiniteQuery,
  useRetranslate,
  useRoomQuery,
  useSendMessage,
} from '../features/chat/api';
import type { ChatMessageDto } from '../features/chat/api';
import {
  dateSeparatorLabel,
  isSameLocalDay,
  localeToLang,
  messageTimeLabel,
} from '../features/chat/format';
import { ImageMessage, imagePathFromPayload } from '../features/chat/ImageMessage';
import { orderEventFromPayload, orderEventLabelKey } from '../features/orders/systemMessage';
import { offerNoticeFromPayload } from '../features/chat/offerNotice';
import { enqueueOutbox, getOutboxEntry, useRoomOutbox } from '../features/chat/outbox';
import type { OutboxEntry } from '../features/chat/outbox';
import { useChatChannel } from '../features/chat/realtime';
import { useAuthStore } from '../stores/auth';

export const Route = createFileRoute('/chat/$roomId')({
  beforeLoad: ({ params }) => requireAuthBeforeLoad(`/chat/${params.roomId}`),
  component: ChatRoomScreen,
});

/** 과거 메시지 추가 로드를 트리거하는 상단 여유 px (col-reverse 기준) */
const LOAD_MORE_THRESHOLD = 200;

type DisplayRow =
  | { kind: 'date'; key: string; label: string }
  | { kind: 'message'; key: string; message: ChatMessageDto }
  | { kind: 'outbox'; key: string; entry: OutboxEntry };

function toBubbleMessage(
  message: ChatMessageDto,
  isMine: boolean,
  myLang: LocaleCode,
  uiLocale: string,
): ChatBubbleMessage {
  const translation = message.translations.find((tr) => tr.target_lang === myLang);

  let translationStatus: ChatBubbleMessage['translationStatus'];
  if (isMine || message.source_lang === null || message.source_lang === myLang) {
    translationStatus = 'not_needed';
  } else if (translation !== undefined) {
    translationStatus = 'done';
  } else if (message.translation_status === 'pending') {
    translationStatus = 'pending';
  } else if (message.translation_status === 'failed') {
    translationStatus = 'failed';
  } else {
    translationStatus = 'not_needed';
  }

  return {
    id: message.id,
    originalText: message.body ?? '',
    originalLang: message.source_lang ?? 'ko',
    translatedText: translation?.translated_text,
    sendState: 'sent',
    translationStatus,
    timeLabel: messageTimeLabel(message.created_at, uiLocale),
  };
}

function outboxBubble(
  entry: OutboxEntry,
  myLang: LocaleCode,
  uiLocale: string,
): ChatBubbleMessage {
  return {
    id: entry.clientMsgId,
    originalText: entry.input.body ?? '',
    originalLang: myLang,
    sendState: entry.status === 'sending' ? 'sending' : 'failed',
    translationStatus: 'not_needed',
    timeLabel: messageTimeLabel(entry.createdAt, uiLocale),
  };
}

function ChatRoomScreen() {
  const { roomId } = Route.useParams();
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = Route.useNavigate();

  const myId = useAuthStore((state) => state.session?.user.id);
  const { data: profile } = useProfile();
  const uiLocale = i18n.language;
  const myLang: LocaleCode = profile?.locale ?? localeToLang(uiLocale);

  const roomQuery = useRoomQuery(roomId);
  const messagesQuery = useMessagesInfiniteQuery(roomId);
  const sendMessage = useSendMessage(roomId);
  const retranslate = useRetranslate(roomId);
  const { mutate: markRead } = useMarkRead(roomId);
  useChatChannel(roomId);

  const roomOutbox = useRoomOutbox(roomId);
  const [text, setText] = useState('');
  const [showOriginal, setShowOriginal] = useState(true);
  const [actionMessageId, setActionMessageId] = useState<string | null>(null);

  /* -------------------------- 메시지 목록 조립 -------------------------- */

  // pages[0]이 최신 청크 → 과거 페이지부터 뒤집어 seq 오름차순 평탄화
  const messages = useMemo(() => {
    const pages = messagesQuery.data?.pages ?? [];
    return [...pages].reverse().flatMap((page) => page.items);
  }, [messagesQuery.data]);

  // 서버 확정된 clientMsgId는 낙관적 목록에서 제외 (Realtime 선착 대비)
  const pendingEntries = useMemo(() => {
    const confirmed = new Set(messages.map((message) => message.client_msg_id));
    return roomOutbox.filter((entry) => !confirmed.has(entry.clientMsgId));
  }, [messages, roomOutbox]);

  const rows = useMemo<DisplayRow[]>(() => {
    const out: DisplayRow[] = [];
    let prevIso: string | null = null;
    const pushDate = (iso: string) => {
      if (prevIso === null || !isSameLocalDay(prevIso, iso)) {
        out.push({ kind: 'date', key: `date-${iso}`, label: dateSeparatorLabel(iso, uiLocale) });
      }
      prevIso = iso;
    };
    for (const message of messages) {
      pushDate(message.created_at);
      out.push({ kind: 'message', key: message.id, message });
    }
    for (const entry of pendingEntries) {
      pushDate(entry.createdAt);
      out.push({ kind: 'outbox', key: entry.clientMsgId, entry });
    }
    return out;
  }, [messages, pendingEntries, uiLocale]);

  /* ----------------------------- 읽음 처리 ----------------------------- */

  const latestSeq = messages.length > 0 ? messages[messages.length - 1]?.seq : undefined;
  const myLastReadSeq = roomQuery.data?.myLastReadSeq;
  const highestSentRef = useRef(0);

  useEffect(() => {
    if (myLastReadSeq !== undefined && myLastReadSeq > highestSentRef.current) {
      highestSentRef.current = myLastReadSeq;
    }
  }, [myLastReadSeq]);

  // 입장 시 + 새 메시지 수신 시 최신 seq로 markRead
  useEffect(() => {
    if (latestSeq === undefined || latestSeq <= highestSentRef.current) return;
    highestSentRef.current = latestSeq;
    markRead(latestSeq);
  }, [latestSeq, markRead]);

  /* ------------------------- 역방향 스크롤/백필 ------------------------- */

  const listRef = useRef<HTMLDivElement | null>(null);

  const maybeLoadOlder = () => {
    const el = listRef.current;
    if (el === null) return;
    if (!messagesQuery.hasNextPage || messagesQuery.isFetchingNextPage) return;
    // col-reverse: scrollTop은 0(하단)에서 음수로 감소 — 상단까지 남은 거리
    const distanceFromTop = el.scrollHeight - el.clientHeight + el.scrollTop;
    if (distanceFromTop < LOAD_MORE_THRESHOLD) {
      void messagesQuery.fetchNextPage();
    }
  };

  // 초기 로드가 화면보다 짧으면 스크롤 이벤트 없이도 이어서 로드
  useEffect(() => {
    const el = listRef.current;
    if (el === null) return;
    if (!messagesQuery.hasNextPage || messagesQuery.isFetchingNextPage) return;
    if (el.scrollHeight <= el.clientHeight) {
      void messagesQuery.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesQuery.data, messagesQuery.hasNextPage, messagesQuery.isFetchingNextPage]);

  /* ------------------------------- 전송 ------------------------------- */

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const body = text.trim();
    if (body === '') return;
    const input: SendMessageInput = { clientMsgId: crypto.randomUUID(), type: 'text', body };
    setText('');
    enqueueOutbox(roomId, input);
    sendMessage.mutate(input);
  };

  const runRetranslate = (messageId: string) => {
    retranslate.mutate(messageId, {
      onError: () => toast.show({ type: 'error', message: t('chat.translationFailed') }),
    });
  };

  // ChatBubble onRetry — 내 실패 메시지면 재전송(멱등), 아니면 재번역
  const handleRetry = (id: string) => {
    const entry = getOutboxEntry(id);
    if (entry !== undefined) {
      enqueueOutbox(roomId, entry.input);
      sendMessage.mutate(entry.input);
      return;
    }
    runRetranslate(id);
  };

  /* ------------------------------- 헤더 ------------------------------- */

  const room = roomQuery.data;
  const isNotFound = roomQuery.error instanceof ApiError && roomQuery.error.status === 404;

  const handleBack = () => {
    if (canGoBack) {
      router.history.back();
    } else {
      void navigate({ to: '/chat' });
    }
  };

  const headerTitle =
    room !== undefined ? (
      <button
        type="button"
        onClick={() =>
          void navigate({ to: '/sourcing/$requestId', params: { requestId: room.request.id } })
        }
        className="flex min-w-0 flex-col items-start rounded-sm text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-600"
      >
        <span className="flex min-w-0 max-w-full items-center gap-1.5">
          <span className="min-w-0 truncate text-title3 text-fg">
            {room.counterpart.display_name}
          </span>
          <TrustBadge
            altitude={room.counterpart.trust_score}
            size="sm"
            meterAriaLabel={t('trust.meterLabel')}
          />
        </span>
        <span className="max-w-full truncate text-caption2 font-normal text-fg-secondary">
          {room.request.title}
        </span>
      </button>
    ) : (
      <Skeleton variant="text" className="w-32" />
    );

  /* ------------------------------- 렌더 ------------------------------- */

  const renderRow = (row: DisplayRow) => {
    switch (row.kind) {
      case 'date':
        return (
          <div key={row.key} className="flex justify-center py-1">
            <span className="text-caption2 text-fg-tertiary">{row.label}</span>
          </div>
        );
      case 'outbox':
        return (
          <ChatBubble
            key={row.key}
            message={outboxBubble(row.entry, myLang, uiLocale)}
            isMine
            retryLabel={t('actions.retry')}
            onRetry={handleRetry}
          />
        );
      case 'message': {
        const message = row.message;
        if (message.type === 'system') {
          const offerNotice = offerNoticeFromPayload(message.payload);
          if (offerNotice !== null) {
            return (
              <div key={row.key} className="flex flex-col items-center py-1">
                <div className="flex w-[85%] flex-col gap-1 rounded-lg border border-line bg-surface px-4 py-3">
                  <span className="text-caption2 font-semibold text-fg-brand">
                    {t('sourcing.offerForm.title')}
                  </span>
                  <span className="text-title3 text-fg tabular-nums">
                    {formatMoney({ amount: offerNotice.priceAmount, currency: offerNotice.currency })}
                  </span>
                  {offerNotice.etaDays !== null ? (
                    <span className="text-caption1 text-fg-secondary">
                      {t('sourcing.etaDays', { count: offerNotice.etaDays })}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          }
          const orderEvent = orderEventFromPayload(message.payload);
          const noticeText =
            orderEvent !== null ? t(orderEventLabelKey(orderEvent.event)) : message.body;
          return (
            <div key={row.key} className="flex flex-col items-center gap-1 py-1">
              <span className="max-w-[85%] rounded-full bg-subtle px-3 py-1 text-center text-caption2 text-fg-secondary">
                {noticeText}
              </span>
              {orderEvent !== null && orderEvent.orderId !== undefined ? (
                <button
                  type="button"
                  onClick={() =>
                    void navigate({
                      to: '/orders/$orderId',
                      params: { orderId: orderEvent.orderId as string },
                    })
                  }
                  className="rounded-sm px-2 py-0.5 text-caption2 font-semibold text-fg-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-600"
                >
                  {t('escrow.actions.viewOrder')}
                </button>
              ) : null}
            </div>
          );
        }
        const isMine = message.sender_id !== null && message.sender_id === myId;
        if (message.type === 'image') {
          return (
            <ImageMessage
              key={row.key}
              imagePath={imagePathFromPayload(message.payload)}
              isMine={isMine}
              timeLabel={messageTimeLabel(message.created_at, uiLocale)}
              alt={t('chat.imageMessage')}
            />
          );
        }
        const bubble = (
          <ChatBubble
            message={toBubbleMessage(message, isMine, myLang, uiLocale)}
            isMine={isMine}
            showOriginal={showOriginal}
            retryLabel={t('actions.retry')}
            onRetry={handleRetry}
          />
        );
        const canRetranslate =
          !isMine && message.source_lang !== null && message.source_lang !== myLang;
        if (!canRetranslate) {
          return <div key={row.key}>{bubble}</div>;
        }
        // 탭하면 ActionSheet('다시 번역') — 내부 버튼(재시도) 클릭은 통과시킨다
        return (
          <div
            key={row.key}
            role="button"
            tabIndex={0}
            aria-label={t('chat.retranslate')}
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('button') !== null) return;
              setActionMessageId(message.id);
            }}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              if ((event.target as HTMLElement).closest('button') !== null) return;
              event.preventDefault();
              setActionMessageId(message.id);
            }}
          >
            {bubble}
          </div>
        );
      }
    }
  };

  return (
    <div className="flex h-dvh flex-col bg-page">
      <TopAppBar
        className="border-b border-line"
        leading={
          <IconButton aria-label={t('actions.back')} onClick={handleBack}>
            <ArrowLeft aria-hidden="true" />
          </IconButton>
        }
        title={headerTitle}
        trailing={
          <IconButton
            aria-label={t('chat.showOriginal')}
            aria-pressed={showOriginal}
            onClick={() => setShowOriginal((prev) => !prev)}
            className={showOriginal ? 'text-gold-600' : undefined}
          >
            <Languages aria-hidden="true" />
          </IconButton>
        }
      />

      {roomQuery.isError && room === undefined ? (
        <EmptyState
          className="mt-16"
          icon={<MessageCircle size={48} strokeWidth={1.5} />}
          title={isNotFound ? t('errors.notFound') : t('errors.generic')}
          action={
            isNotFound ? undefined : (
              <Button variant="tertiary" onClick={() => void roomQuery.refetch()}>
                {t('actions.retry')}
              </Button>
            )
          }
        />
      ) : (
        <>
          <div
            ref={listRef}
            onScroll={maybeLoadOlder}
            className="flex flex-1 flex-col-reverse overflow-y-auto"
          >
            <div className="flex flex-col gap-2 px-4 pt-2 pb-3">
              {messagesQuery.isPending ? (
                <div className="flex justify-center py-8">
                  <Spinner label={t('common.loading')} />
                </div>
              ) : messagesQuery.isError ? (
                <EmptyState
                  title={t('errors.generic')}
                  action={
                    <Button variant="tertiary" onClick={() => void messagesQuery.refetch()}>
                      {t('actions.retry')}
                    </Button>
                  }
                />
              ) : (
                <>
                  {messagesQuery.isFetchingNextPage ? (
                    <div className="flex justify-center py-2">
                      <Spinner size="sm" label={t('common.loading')} />
                    </div>
                  ) : null}
                  {messagesQuery.hasNextPage ? null : (
                    <div className="flex justify-center px-4 py-2">
                      <p className="rounded-lg bg-subtle px-3 py-2 text-center text-caption2 text-fg-secondary">
                        {t('chat.escrowNotice')}
                      </p>
                    </div>
                  )}
                  {rows.map(renderRow)}
                </>
              )}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex items-center gap-2 border-t border-line bg-page px-3 pt-2 pb-[calc(var(--safe-bottom)+8px)]"
          >
            <Input
              value={text}
              onChange={(event) => setText(event.target.value)}
              placeholder={t('chat.inputPlaceholder')}
              maxLength={1000}
              autoComplete="off"
              enterKeyHint="send"
              aria-label={t('chat.inputPlaceholder')}
            />
            <IconButton
              type="submit"
              variant="subtle"
              aria-label={t('chat.send')}
              disabled={text.trim() === ''}
            >
              <Send aria-hidden="true" />
            </IconButton>
          </form>
        </>
      )}

      <ActionSheet
        open={actionMessageId !== null}
        onOpenChange={(open) => {
          if (!open) setActionMessageId(null);
        }}
        actions={[
          {
            key: 'retranslate',
            label: t('chat.retranslate'),
            icon: <RotateCcw className="size-5" aria-hidden="true" />,
          },
        ]}
        onSelect={(key) => {
          if (key === 'retranslate' && actionMessageId !== null) {
            runRetranslate(actionMessageId);
          }
        }}
        cancelLabel={t('actions.cancel')}
      />
    </div>
  );
}
